"use client";

import React, { useState } from "react";
import { Building2, Clock, Hammer, Briefcase, TrendingUp, ShieldCheck, ChevronDown, Landmark, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  title: "Rental Portfolio Financing and Cash Out Refinance",
  description: "Long-term, fixed-rate capital for stabilized rental portfolios. Optimized for cash flow and asset appreciation.",
  ltv: "Up to 80% LTV",
  terms: "5, 10, 30 Year Fixed",
  speed: "30 - 45 Days",
  icon: Building2
}, {
  title: "Bridge & Transitional",
  description: "Short-term liquidity for acquisitions, lease-ups, and time-sensitive opportunities requiring speed and flexibility. This also includes Foreclosure Bailout Loans",
  ltv: "Up to 75% LTV",
  terms: "12 - 36 Months",
  speed: "2 - 3 Weeks",
  icon: Clock
}, {
  title: "Fix & Flip Capital",
  description: "High-leverage renovation capital for residential and multifamily rehabilitation projects.",
  ltv: "Up to 93% LTC",
  terms: "12 - 24 Months",
  speed: "10 - 14 Days",
  icon: Hammer
}, {
  title: "Construction & Development",
  description: "Ground-up construction financing for experienced developers across residential and commercial asset classes.",
  ltv: "Up to 85% LTC",
  terms: "18 - 36 Months",
  speed: "30 - 60 Days",
  icon: Landmark
}, {
  title: "Business Working Capital",
  description: "Revenue-based financing and lines of credit to smooth cash flow and fund operational expansion.",
  ltv: "Revenue Based",
  terms: "6 - 24 Months",
  speed: "24 - 48 Hours",
  icon: Briefcase
}, {
  title: "Structured Term Loans",
  description: "Senior and mezzanine debt structures for stabilizing businesses and refinancing existing obligations. This also includes soft money loans",
  ltv: "Asset/Cash Flow Based",
  terms: "1 - 30 Years",
  speed: "1 - 2 Weeks",
  icon: TrendingUp
}, {
  title: "Private Credit & Institutional",
  description: "Bespoke capital solutions from non-bank institutions for complex, high-value transactions. This also includes Stated Income / No Income Verification Loans, LOW DOC Loans and Foreign National Programs",
  ltv: "Deal Dependent",
  terms: "Custom Structure",
  speed: "4 - 8 Weeks",
  icon: Building2
}];
export default function FinancingSolutionsGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="financing-solutions-grid" ref={ref} className="dark bg-background text-foreground py-24 px-2 sm:px-4 md:px-6 lg:px-8 relative overflow-hidden border-t border-border/40">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.03] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className={`mb-20 max-w-3xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
            Our Capital Solutions
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-light leading-relaxed">Institutional-grade financing structures designed for speed, scale, and strategic execution across real estate and business sectors. Special funding includes: Foreign National Programs, Cash Out Refinance, Stated Income / No Income Verification Loans, Soft Money Loans, LOW DOC Loans, Foreclosure Bailout Loans, and etc.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8 border-t border-l border-border/60">
          {solutions.map((solution, i) => <div key={i} data-index={i} className={`w-full group relative border-r border-b border-border/60 bg-background/50 p-4 sm:p-6 transition-all duration-500 hover:bg-card/30 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 50}ms`
        }}>
              <div className="flex flex-col h-full justify-between gap-8">
                <div className="space-y-6">
                  <div className="flex items-start justify-between">
                    <div className="p-2.5 rounded-sm bg-secondary/10 border border-border/40 text-foreground">
                      <solution.icon className="w-5 h-5 stroke-[1.5]" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-medium text-foreground tracking-tight">
                      {solution.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed font-light">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <Collapsible className="w-full group/collapsible">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent hover:text-primary text-muted-foreground font-mono text-xs md:text-[10px] uppercase tracking-widest border-t border-border/40 pt-4 rounded-none h-auto transition-colors">
                      <span>View Deal Parameters</span>
                      <ChevronDown className="h-3 w-3 transition-transform duration-300 group-data-[state=open]/collapsible:rotate-180" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <div className="pt-4 space-y-3">
                      <div className="flex justify-between items-center text-xs border-b border-border/20 pb-2">
                        <span className="text-muted-foreground font-light">
                          Max LTV/LTC
                        </span>
                        <span className="font-mono text-foreground font-medium">
                          {solution.ltv}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs border-b border-border/20 pb-2">
                        <span className="text-muted-foreground font-light">
                          Terms
                        </span>
                        <span className="font-mono text-foreground font-medium">
                          {solution.terms}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs pt-1">
                        <span className="text-muted-foreground font-light">
                          Closing Speed
                        </span>
                        <span className="font-mono text-foreground font-medium">
                          {solution.speed}
                        </span>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}